import { Component, Inject, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { forkJoin, of } from "rxjs";
import { getEnumLookup } from "src/app/_helpers/enum-helper";
import { ControlStatusEnum } from "src/app/enums/control-status.enum";
import { OperatorTypeEnum } from "src/app/enums/operatorType.enum";
import { Control } from "src/app/models/control.model";
import { ControlAgentDto } from "src/app/models/controlAgent.model";
import { ControlOrganizationLevelSeg } from "src/app/models/controlOrganizationLevelSeg.model";
import { RiskRegister } from "src/app/models/risk-assessment.model";
import { ControlOrganizationLevelSegService } from "src/app/services/control-organization-level-seg.service";
import { ControlService } from "src/app/services/control.service";
import { DateFormatService } from "src/app/services/dateFormat.service";
import { ToastService } from "src/app/services/toast.service";
import { ColumnSpan } from "src/app/shared/form/columnSpan.model";
import {
    DateFormField,
    SingleSelectionFormField,
    TextAreaFormField,
} from "src/app/shared/form/formField.model";
import { ILookup } from "src/app/shared/form/lookup.model";
import nameOf from "src/app/shared/form/nameOf.helper";

import { trackByIndex } from "src/app/shared/utils/track-by.util";
export class ILinkControlDialogData {
    id: string;
    riskDetails: RiskRegister;
    isAssessment: boolean;
}

@Component({
  standalone: false,
    selector: "app-link-control-dialog",
    templateUrl: "./link-control-dialog.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        "./../../shared/form/form.component.scss",
        "./link-control-dialog.component.scss",
    ],
})
export class LinkControlDialogComponent implements OnInit {
    title: string;
    isFormLoading = false;
    isLoading = false;
    isReadonly = false;
    fields: any[];
    organizationLevels: ILookup[];
    segs: ILookup[];
    agents: ILookup[];
    entity: any;
    segOrgLevelId: string;
    private existingOwner: Pick<ControlOrganizationLevelSeg, 'ownerUserId' | 'ownerName'> = {};
    linkedControls: ControlAgentDto[] = [];

    constructor(
        public dialogRef: MatDialogRef<LinkControlDialogComponent>,
        private dateFormatService: DateFormatService,
        private toastService: ToastService,
        private controlOrganizationLevelSegService: ControlOrganizationLevelSegService,
        private controlService: ControlService,
        @Inject(MAT_DIALOG_DATA) public data: ILinkControlDialogData,
    ) {}

    ngOnInit() {
        this.title = "Link Control to SEG, Org Level, and Agent";
        this.setData();
        this.initFields();
    }

    setData() {
        this.entity = new ControlOrganizationLevelSeg();
        this.entity.segId = this.data.riskDetails.segId;
        this.entity.organizationLevelId =
            this.data.riskDetails.organizationLevelId;
        this.entity.agentId = this.data.riskDetails.agentId;
        // Set status based on whether it's an assessment or planned control
        this.entity.status = this.data.isAssessment
            ? ControlStatusEnum.Implemented
            : ControlStatusEnum.Planned;
        this.segs = [
            {
                id: this.data.riskDetails.segId,
                name: this.data.riskDetails.segName,
            },
        ];
        this.organizationLevels = [
            {
                id: this.data.riskDetails.organizationLevelId,
                name: this.data.riskDetails.organizationLevelId.toString(),
            },
        ];
        this.agents = [
            {
                id: this.data.riskDetails.agentId,
                name: this.data.riskDetails.agentName,
            },
        ];
        const filterBySeg = {
            OperatorTypeEnum: JSON.stringify({
                Condition: OperatorTypeEnum.Equal.toString(),
                Field: "controlId",
                Value: this.data.id,
            }),
            organizationLevelId: this.data.riskDetails.organizationLevelId,
            segId: this.data.riskDetails.segId,
        };

        this.controlService.getById(this.data.id).subscribe({
            next: (control) => {
                this.linkedControls = control.controlAgents;
            },
            error: this.handleError,
        });

        this.controlOrganizationLevelSegService
            .get("", "", "asc", 0, 10, JSON.stringify(filterBySeg))
            .subscribe({
                next: (result) => {
                    if (result.data.length > 0) {
                        const first = result.data[0];
                        const toLocal = (date?: string): string | undefined => {
                            if (!date) {
                                return undefined;
                            }

                            return this.dateFormatService.formatDateTimeToString(
                                this.dateFormatService.parseDateComponents(
                                    date,
                                ),
                                this.dateFormatService.canonicalDateFormat,
                            );
                        };

                        this.entity.dateImplementationStart = toLocal(first.dateImplementationStart);
                        this.entity.dateImplementationComplete = toLocal(first.dateImplementationComplete);
                        this.entity.targetImplementationStart = toLocal(first.targetImplementationStart);
                        this.entity.targetImplementationEnd = toLocal(first.targetImplementationEnd);
                        this.entity.notes = first.notes;
                        this.segOrgLevelId = first.id;
                        this.existingOwner = { ownerUserId: first.ownerUserId, ownerName: first.ownerName };
                    }
                },
                error: this.handleError,
            });
    }

    initFields(): void {
        const isRiskAssessment = this.data.isAssessment;
        const allowedControlStatus = getEnumLookup(ControlStatusEnum).filter(
            (cs) =>
                isRiskAssessment
                    ? cs.id === ControlStatusEnum.Implemented
                    : cs.id === ControlStatusEnum.Planned ||
                      cs.id === ControlStatusEnum.Ongoing,
        );
        this.fields = [
            new SingleSelectionFormField({
                property: nameOf<ControlOrganizationLevelSeg>((x) => x.segId),
                options: this.segs,
                name: "SEG",
                readonly: true,
                columnSpan: new ColumnSpan(1, 3),
            }),
            new SingleSelectionFormField({
                property: nameOf<ControlOrganizationLevelSeg>(
                    (x) => x.organizationLevelId,
                ),
                options: this.organizationLevels,
                name: "Organization Level",
                readonly: true,
                columnSpan: new ColumnSpan(1, 3),
            }),
            new SingleSelectionFormField({
                property: nameOf<RiskRegister>((x) => x.agentId),
                options: this.agents,
                name: "Agent",
                readonly: true,
                columnSpan: new ColumnSpan(1, 3),
            }),
            new SingleSelectionFormField({
                property: nameOf<Control>((x) => x.status),
                options: allowedControlStatus,
                name: "Status",
                columnSpan: new ColumnSpan(1, 3),
                readonly: isRiskAssessment,
            }),
            new DateFormField({
                property: nameOf<ControlOrganizationLevelSeg>(
                    (x) => x.dateImplementationStart,
                ),
                name: "Implementation Start Date",
                required: false,
                columnSpan: new ColumnSpan(1, 3),
            }),
            new DateFormField({
                property: nameOf<ControlOrganizationLevelSeg>(
                    (x) => x.dateImplementationComplete,
                ),
                name: "Implementation Complete Date",
                required: false,
                columnSpan: new ColumnSpan(1, 3),
            }),
            new DateFormField({
                property: nameOf<ControlOrganizationLevelSeg>(
                    (x) => x.targetImplementationStart,
                ),
                name: "Target Implementation Start Date",
                required: false,
                columnSpan: new ColumnSpan(1, 3),
            }),
            new DateFormField({
                property: nameOf<ControlOrganizationLevelSeg>(
                    (x) => x.targetImplementationEnd,
                ),
                name: "Target Implementation End Date",
                required: false,
                columnSpan: new ColumnSpan(1, 3),
            }),
            new TextAreaFormField({
                property: nameOf<ControlOrganizationLevelSeg>((x) => x.notes),
                name: "Notes",
                required: false,
                columnSpan: new ColumnSpan(1, 1),
            }),
        ];
    }

    skipLinking() {
        this.dialogRef.close(true);
    }

    cancel() {
        this.dialogRef.close(false);
    }

    onBeforeSave(model: ControlOrganizationLevelSeg): void {
        const toCanonicalDate = (dateValue?: string): string | undefined => {
            const normalizedValue = dateValue?.trim();

            if (!normalizedValue) {
                return undefined;
            }

            return this.dateFormatService.parseUserDateToYMD(normalizedValue) ?? undefined;
        };

        model.dateImplementationStart = toCanonicalDate(model.dateImplementationStart);
        model.dateImplementationComplete = toCanonicalDate(
            model.dateImplementationComplete,
        );
        model.targetImplementationStart = toCanonicalDate(
            model.targetImplementationStart,
        );
        model.targetImplementationEnd = toCanonicalDate(model.targetImplementationEnd);
    }

    validate(): boolean {
        return true;
    }

    save(model = this.entity) {
        if (!this.validate()) return;

        this.isLoading = true;
        this.onBeforeSave(model);

        const controlId = this.data.id;
        const alreadyLinked = this.linkedControls.some(
            (x) => x.controlId === controlId && x.agentId === model.agentId,
        );

        const controlAgentReq = alreadyLinked
            ? null
            : this.controlService.patch(controlId, {
                  controlAgents: [
                      ...this.linkedControls,
                      { controlId: controlId, agentId: model.agentId },
                  ],
              });

        const segOrgPayload: any = {
            controlId: controlId,
            organizationLevelId: model.organizationLevelId,
            segId: model.segId,
            status: model.status,
            dateImplementationStart: model.dateImplementationStart,
            dateImplementationComplete: model.dateImplementationComplete,
            targetImplementationStart: model.targetImplementationStart,
            targetImplementationEnd: model.targetImplementationEnd,
            notes: model.notes,
        };

        const segOrgReq = !this.segOrgLevelId
            ? this.controlOrganizationLevelSegService.create(segOrgPayload)
            : this.controlOrganizationLevelSegService.update({
                  id: this.segOrgLevelId,
                  ...segOrgPayload,
                  ...this.existingOwner,
              });

        forkJoin({
            segOrg: segOrgReq,
            controlAgent: controlAgentReq ?? of(null),
        }).subscribe({
            next: () => {
                this.isLoading = false;
                this.dialogRef.close(model);
            },
            error: this.handleError,
        });
    }

    handleError = (error: any): void => {
        this.isLoading = false;
        this.toastService.showError(error);
    };

    public trackByIndex = trackByIndex;
}
